import React, { useContext, useEffect, useState } from 'react';
import { navigate } from '@reach/router';
import { pdf } from '@react-pdf/renderer';

import { Alert } from '@material-ui/lab';

import AuthContext from '../../../services/firebase/AuthContext';
import FireDataContext from '../../../services/firebase/FireDataContext';
import ReferralQuoteContent from './ReferralQuoteContent';
import { tariffs } from '../../billing/timesheets/BillingTariff';
import ServiceInvoice from '../../billing/timesheets/ServiceInvoice';
import { timestamp } from '../../../app/data/validate';
import MemberMiniMenu from '../menus/MemberMiniMenu';
import TariffPicker from './TariffPicker';
import QuotePDF from './pdf/QuotePDF';
import { createBlobAttachment } from '../../../services/firebase/StorageService';
import { storeBookingForm } from './docx/BookingFormDoc';
import { keyGen } from '../../../services/keygen';
import { DbxContext } from '../../../services/DbxContext';

const ReferralQuotePage = ({ referralId }) => {
    const { currentUser } = useContext(AuthContext);
    const db = useContext(FireDataContext);
    const cache = useContext(DbxContext);

    const [message, setMessage] = useState(undefined);
    const [request, setRequest] = useState();
    const [referral, setReferral] = useState();
    const [quote, setQuote] = useState();
    const [existingQuote, setExistingQuote] = useState();
    const [notes, setNotes] = useState();
    const [courtCase, setCourtCase] = useState();
    const [courtInfo, setCourtInfo] = useState();
    const [intermediary, setIntermediary] = useState();
    const [tariffCode, setTariffCode] = useState();
    const [items, setItems] = useState();
    const [ready, setReady] = useState(false);
    const [transaction] = useState(keyGen(10));

    useEffect(() => {
        const fetch = async () => {
            try {
                const path = `intermediaries/${currentUser.uid}/referrals/${referralId}`;

                const rfq = await db.get({ path });
                if (!rfq) return setMessage(`rfq not found: ${path}`);
                setRequest(rfq);

                const ref = await db.get({ path: rfq.referralPath });
                if (!ref) return setMessage(`referral not found: ${rfq.referralPath}`);

                const referrer = await db.get({ path: `users/${ref.uid}` });
                setReferral({ ...ref, referrer });

                const usr = await db.get({ path: `users/${currentUser.uid}` });
                const im = await db.get({ path: `intermediaries/${currentUser.uid}` });

                im.profile.email = im.profile.email || currentUser.email;
                im.profile.title = im.profile.title || usr.name;
                setIntermediary(im);

                const memos = await db.get({ path: `organizations/${ref.organizationId}/notes/${referralId}` });
                setNotes(memos || []);

                const proceedings = await db.get({ path: `organizations/${ref.organizationId}/cases/${ref.courtCaseId}` });
                setCourtCase(proceedings);

                const existing = await db.get({ path: `intermediaries/${currentUser.uid}/quotes/${referralId}` });

                if (existing && tariffs.has(existing?.tariffCode)) {
                    setQuote(new ServiceInvoice(tariffs.get(existing?.tariffCode), existing?.items || []));
                    setExistingQuote(existing);
                    setTariffCode(existing?.tariffCode);
                }

                setCourtInfo(existing?.court ?? {});

                setItems(existing?.items || []);
                setReady(true);
            }
            catch (error) {
                console.log(error);
                setMessage(error.message);
            }
        }

        fetch();

    }, [db, referralId, currentUser.uid, currentUser.email])


    const handleChange = () => {
        (async () => {
            const payload = {
                tariffCode: tariffCode,
                items: quote.items().filter(({ key }) => key !== 'ADMIN01'),
                status: existingQuote?.status ?? 'draft',
                digest: {
                    message: request.message,
                    bookingReference: referral.bookingReference,
                    courtCaseNumber: courtCase.courtCaseNumber,
                    organization: request.organization,
                    referral: {
                        id: referralId,
                        ts: referral.ts
                    },
                    request: {
                        id: request.id,
                        ts: request.ts
                    }
                },
                ts: timestamp()

            };

            await db.update({
                path: `intermediaries/${currentUser.uid}/quotes/${referralId}`,
                payload
            })
                .then()
                .catch(error => console.log(error, payload));
            //console.log('handleChange', quote.items())

            await db.update({
                path: `intermediaries/${currentUser.uid}/referrals/${referralId}`,
                payload: {
                    status: 'active'
                }
            })
                .catch(error => console.log(error));

            if (existingQuote?.status === 'submitted') {
                const submittedQuoteKey = `${referral.id.slice(0, 10)}${intermediary.id.slice(0, 10)}`;

                await db.update({
                    path: `organizations/${referral.organizationId}/quotes/${submittedQuoteKey}`,
                    payload: { intermediary, tariff: tariffs.get(tariffCode), referral, request, courtCase, items: quote.items(), notes, status: 'accepted', ts: timestamp() }
                });
            }

        })();
    }

    const handleCourtChange = court => {
        const payload = {
            court,
            ts: timestamp()
        };

        db.update({
            path: `intermediaries/${currentUser.uid}/quotes/${referralId}`,
            payload
        })
            .then(setCourtInfo(court))
            .catch(error => console.log(error, payload));
    }

    const qDoc = (<QuotePDF digest={{ intermediary, referral, request, courtCase, quote, notes }} />);

    // const updatePdfDoc = () => {
    //     return new Promise((resolve, reject) => {
    //         try {
    //             updatePDF();
    //             while (pdf.loading) {
    //                 // yuk
    //                 if (pdf.error) {
    //                     console.log('pdf', pdf.error);
    //                 }
    //             };
    //             resolve(true);
    //         }
    //         catch (error) {
    //             console.log(error);
    //             reject(error);
    //         }
    //     })
    // }

    const handleQuoteSubmit = ({ email, docs }) => {
        const digest = { intermediary, tariff: tariffs.get(tariffCode), referral, request, courtCase, items: quote.items(), notes, status: 'submitted', ts: timestamp() };

        const submittedQuoteKey = `${referral.id.slice(0, 10)}${intermediary.id.slice(0, 10)}`;

        (async () => {
            try {
                // await updatePdfDoc();
                const blob = await pdf(qDoc).toBlob();
                // 10 Jun 2021 we realised that duplicate quotes would be created when the invoice was re-submitted
                // this was because we were doing an insert as there was no natural key to use for the quote
                // we couldn't use the rfq id because that was also the referral id which could have gone to many intermediaries
                // It also became apparent that intermediaries would be over-writing each others quotes for the same referral - ouch!
                // and so ...
                if (existingQuote?.quotePath) {
                    await db.remove({ path: existingQuote.quotePath });
                }



                await db.update({
                    path: `organizations/${referral.organizationId}/quotes/${submittedQuoteKey}`,
                    payload: digest
                });

                const url = `https://ti-coop.web.app/quotes/${submittedQuoteKey}`;

                const terms = await db.get({ path: 'config/terms', context: 'handleQuoteSubmit' });

                // always send the T&Cs
                const attachments = [{
                    path: tariffCode === 'tier1' ? terms.tier1 : terms.laa
                }];

                // TODO: get rid of these string literals for Quote and Booking Form! - they're repeated in the quote content form !!!
                if (docs.get('Quote')) {
                    console.log('pdf quote', blob)
                    const path = await createBlobAttachment({ fullPath: `quotes/${referral.organizationId}/${submittedQuoteKey}.pdf`, blob: blob });
                    attachments.push({ path });
                }

                if (docs.get('Booking Form')) {
                    const path = await storeBookingForm({ intermediary, referral, request, courtCase, quote, notes, court: courtInfo, existingQuote })
                    attachments.push({ path });
                }

                const psText = `\r\nHMCTS have asked that, if there is time, a Purchase Order is raised for this work. \r\nFor guidance they have offered these links; \r\nFull Guide: https://intranet.justice.gov.uk/documents/2021/09/paying-non-registered-intermediaries-full-guide.pptx
                \r\nQuick Guide: https://intranet.justice.gov.uk/documents/2021/09/paying-non-registered-intermediaries-quick-guide.pdf`;

                const psHtml = `<p>HMCTS have asked that, if there is time, a Purchase Order is raised for this work.</p><p>For guidance they have offered these links;</p><p>Full Guide: https://intranet.justice.gov.uk/documents/2021/09/paying-non-registered-intermediaries-full-guide.pptx</p>
                <p>Quick Guide: https://intranet.justice.gov.uk/documents/2021/09/paying-non-registered-intermediaries-quick-guide.pdf</p>`;

                const mail = {
                    to: [email.to],
                    cc: [currentUser.email],
                    message: {
                        subject: `Your quote for Booking Reference ${referral.bookingReference} Case Number ${courtCase.courtCaseNumber}`,
                        html: `<div>Your quotation is attached to this mail and can also can now be accessed <a href="${url}">here</a></div><div>Alternatively, you can sign in to TiCiT and go your quotes page</div><p>${email.message}${psHtml}</p>`,
                        text: `Your quotation is attached and can also be accessed at this URL ${url} \r\nAlternatively, you can sign in to TiCiT and go the Quotes page \r\n${email.message}\r\n${psText}`,
                        attachments
                    },
                    ts: timestamp(),
                    uid: currentUser.uid
                }

                if (email.cc) {
                    mail.cc.push(email.cc);
                }

                const ack = await db.insert({
                    path: 'mail',
                    payload: mail
                });

                await Promise.all([
                    db.update({
                        path: `intermediaries/${currentUser.uid}/referrals/${referralId}`,
                        payload: {
                            status: 'completed'
                        }
                    }),
                    db.update({
                        path: `intermediaries/${currentUser.uid}/quotes/${referralId}`,
                        payload: {
                            mailId: ack.id,
                            status: 'submitted',
                            quotePath: `organizations/${referral.organizationId}/quotes/${submittedQuoteKey}`,
                            ts: timestamp()
                        }
                    })
                ]);

                cache.publish({
                    topic: transaction,
                    value: true
                })

            }
            catch (error) {
                console.log(error)
            }

        })();
    }

    const handleConfirmBooking = () => {

        // HACK: this code was copied from the ReferrerQuotePage as a temporary solution prior to omni going live
        // NB: the paths to the data have been changed and a new surrogate key introduced
        const ts = timestamp();
        const payload = {
            items: quote.items(),
            referral,
            tariff: tariffs.get(tariffCode),
            created: {
                uid: currentUser.uid,
                ts
            }
        };

        try {
            // use composite key to reduce risk of duplicates :O
            const surrogateKey = `${referral.organizationId.slice(0, 10)}${existingQuote.id.slice(0, 10)}`;
            const orgBookingPath = `organizations/${referral.organizationId}/bookings/${surrogateKey}`;

            db.update({
                path: orgBookingPath,
                payload
            })
                .then(() => {
                    const booking = {
                        bookingId: surrogateKey,
                        bookingPath: orgBookingPath,
                        bookedBy: currentUser.uid,
                        bookedAt: ts
                    };

                    (async () => {
                        // update the organization's version of the quote & referral
                        await db.update({
                            path: existingQuote.quotePath,
                            payload: { booking, status: 'booked' }
                        });

                        await db.update({
                            path: `organizations/${referral.organizationId}/referrals/${referral.id}`,
                            payload: { booking, status: 'booked' }
                        });

                        // create a reference to the booking for the member so they can easily get their own bookings
                        await db.update({
                            path: `intermediaries/${currentUser.uid}/bookings/${surrogateKey}`,
                            payload: {
                                ...payload,
                                booking,
                            }
                        });

                        await db.update({
                            path: `intermediaries/${currentUser.uid}/referrals/${referral.id}`,
                            payload: {
                                booking
                            }
                        });

                        // update the member's version of the quote
                        await db.update({
                            path: existingQuote._docPath,
                            payload: { booking }
                        });

                    })()
                        .then(() => navigate("/"))
                        .catch(console.log);

                })
        }
        catch (error) {
            console.log(error)
        }


    }

    const handleTariffSelection = key => {
        setQuote(new ServiceInvoice(tariffs.get(key), items || []));
        setTariffCode(key);
    }

    const handleCaseNumberChange = value => {
        db.update({
            path: courtCase._docPath,
            payload: {
                courtCaseNumber: value,
                ts: timestamp(),
                uid: currentUser.uid
            },
            context: 'ReferralQuotePage'
        })
            .catch(error => console.log(error));
    }

    const handleDeleteDraft = () => {
        if (existingQuote.status !== 'draft') return;

        (async () => {
            try {
                await db.remove({ path: `intermediaries/${currentUser.uid}/quotes/${referralId}`, context: `ReferralQuotePage` });
                await db.update({ path: `intermediaries/${currentUser.uid}/quotes/${referralId}`, payload: { status: 'new' }, context: 'ReferralQuotePage' });
                navigate(`/casework/referral/${referralId}`);
            }
            catch (error) {
                console.log(error);
            }
        })();
    }

    if (!ready) return <></>;
    if (message) return (<MemberMiniMenu><Alert severity="error" variant="filled">{message}</Alert></MemberMiniMenu>);

    return (
        <MemberMiniMenu>
            {
                tariffCode &&
                <ReferralQuoteContent
                    onChange={handleChange}
                    onQuoteSubmit={handleQuoteSubmit}
                    onCourtChange={handleCourtChange}
                    onCaseNumberChange={handleCaseNumberChange}
                    onConfirmBooking={handleConfirmBooking}
                    onDeleteDraft={handleDeleteDraft}
                    referral={referral}
                    request={request}
                    quote={quote}
                    tariff={tariffs.get(tariffCode)}
                    notes={notes}
                    courtCase={courtCase}
                    intermediary={intermediary}
                    existingQuote={existingQuote}
                    court={courtInfo}
                    transaction={transaction}
                />
            }
            {
                ready && !tariffCode &&
                <TariffPicker onContinue={handleTariffSelection} />
            }
        </MemberMiniMenu>
    )
};

export default ReferralQuotePage;